/**
 * 008 · US6 (FR-015) — Mensaje de WhatsApp para compartir la invitación.
 *
 * La pareja puede configurar una plantilla (weddings.whatsapp_message_template) con variables;
 * si no hay plantilla, se usa un texto por defecto cálido. Variables soportadas:
 *   {invitado} / {rotulo}  → rótulo/nombre del invitado (alias equivalentes)
 *   {novios}               → nombres de la pareja (en el orden elegido)
 *   {fecha}                → fecha de la boda (texto ya formateado, opcional)
 *   {link}                 → link personal del invitado
 * Mínimos recomendados: {rotulo} y {link}. Si la plantilla los omite, el editor lo ADVIERTE
 * (`missingWhatsAppMinimums`); no se modifica el mensaje — la decisión es de la pareja.
 */
export interface WhatsAppMessageVars {
  invitado: string;
  novios: string;
  fecha?: string;
  link: string;
}

// Nota: sin emojis en el default — WhatsApp click-to-chat (wa.me ?text=) puede corromper
// emojis de 4 bytes a "�". La pareja puede añadirlos en su plantilla personalizada bajo su riesgo.
const DEFAULT_TEMPLATE =
  '¡Hola {invitado}! Con mucha ilusión te invitamos a nuestra boda. ' +
  'Nos encantaría que nos acompañes en este día tan especial. ' +
  'Aquí puedes ver la invitación y confirmar tu asistencia: {link}';

export function buildWhatsAppMessage(
  vars: WhatsAppMessageVars,
  template?: string | null,
): string {
  const tpl = (template && template.trim()) || DEFAULT_TEMPLATE;
  // Sustitución fiel: no modificamos el mensaje de la pareja. Si su plantilla omite el
  // rótulo o el link, se advierte en el editor (ver `missingWhatsAppMinimums`), no se inyecta.
  return tpl
    .replace(/\{(invitado|rotulo)\}/gi, vars.invitado || '')
    .replace(/\{novios\}/gi, vars.novios || '')
    .replace(/\{fecha\}/gi, vars.fecha || '')
    .replace(/\{link\}/gi, vars.link || '')
    .trim();
}

/**
 * Qué mínimos ({rotulo}, {link}) faltan en una plantilla personalizada — para ADVERTIR en el
 * editor (no se corrige automáticamente; la decisión es de la pareja).
 */
export function missingWhatsAppMinimums(template: string | null | undefined): {
  rotulo: boolean;
  link: boolean;
} {
  const t = template ?? '';
  return {
    rotulo: !/\{(invitado|rotulo)\}/i.test(t),
    link: !/\{link\}/i.test(t),
  };
}

export { DEFAULT_TEMPLATE as DEFAULT_WHATSAPP_TEMPLATE };
